import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { Divider, IconButton, useTheme, Avatar } from '@mui/material';
import { AxiosError } from 'axios';
import { observer } from 'mobx-react';
import { Button } from '@components/index';
import { ProfileModal } from '@pages/customer/common/modals';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { ChevronIcon } from '@icons/ChevronIcon';
import { PencilIcon } from '@icons/PencilIcon';
import { TrashIcon } from '@icons/TrashIcon';
import type { SubscriptionConnectionModel } from '@repositories/subscriptionConnectionRepository/models/SubscriptionConnectionModel';
import type { ConnectionStatus } from '@repositories/subscriptionConnectionRepository/subscriptionConnection.types';
import { useGetSubscription } from '@queryHooks/useSubscription';
import {
  useConnect,
  useConnectIsMutating,
  useConnectWithOauth,
  useDeleteSubscriptionConnection,
  useDisconnect,
  useGetSubscriptionConnection,
} from '@queryHooks/useSubscriptionConnection';
import { ConnectionInfo } from './ConnectionInfo';
import { SubscriptionInfo } from './SubscriptionInfo';
import { UsageWorkflowInfo } from './UsageWorkflowInfo';
import { AppLogin } from './appLogin';

type ModalState = {
  isOpen: boolean;
  type: 'new' | 'modify';
};

export const ConnectionDetail = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const theme = useTheme();
  const {
    categoryStore,
    uiStore: { alertStore, toastStore },
    authStore,
  } = useStore();
  const prevConnectionStatus = useRef<ConnectionStatus>();
  const lastRefetchTime = useRef<number | null>(null);

  const { mutate: deleteConnection } = useDeleteSubscriptionConnection(params.subscriptionId as string);

  const { data: subscription, isLoading: isSubscriptionLoading } = useGetSubscription(params.subscriptionId as string);
  const { data: connection, isFetching: isConnectionFetching } = useGetSubscriptionConnection(
    params.subscriptionId as string,
    {
      enabled: !!subscription?.connection?.connectionId.length,
      cacheTime: 0,
      refetchInterval: (data: SubscriptionConnectionModel | undefined) => {
        const now = Date.now();
        if (data?.status === 'CONNECTING') {
          if (lastRefetchTime.current === null || now - lastRefetchTime.current >= 60000) {
            // 1분 (60초) 타임아웃
            lastRefetchTime.current = now;
            return 3000; // 3초 간격으로 refetch
          }
          return 3000;
        }
        if (data?.status === 'FAILED') {
          lastRefetchTime.current = null;
          return false;
        }
        lastRefetchTime.current = null;
        return false;
      },
      retry: false,
      onSuccess: data => {
        if (
          !!prevConnectionStatus.current &&
          prevConnectionStatus.current !== 'CONNECTED' &&
          data?.status === 'CONNECTED'
        ) {
          toastStore.open({
            message: t('Subscrib_Connect_Create_14'),
            hideAction: true,
          });
        }
        prevConnectionStatus.current = data?.status;
      },
    },
  );

  const { mutate: disconnect } = useDisconnect();
  const { mutate: connect } = useConnect(subscription?.id ?? '');
  const connectMutatingCount = useConnectIsMutating(subscription?.id ?? '');
  const { refetch } = useConnectWithOauth(subscription?.id as string, {
    enabled: false,
  });

  const [isModalOpen, setIsModalOpen] = useState<ModalState>({ isOpen: false, type: 'new' });
  const [popup, setPopup] = useState<Window | null>(null);
  const redirectUrl = `${window.location.origin}/oauth-callback`;

  const [ownerProfileAnchorEl, setOwnerProfileAnchorEl] = useState<HTMLElement | null>(null);

  const oauthMessageListener = useCallback(
    (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type !== 'oauth-callback') return;

      const { code, codeVerifier } = e.data ?? {};
      if (code) {
        connect(
          {
            subscriptionId: subscription?.id,
            useAsyncJob: true,
            oauthParams: {
              authCode: code,
              redirectUrl,
              codeVerifier: codeVerifier || undefined,
            },
          },
          {
            onSuccess: data => {
              if (data.status === 'FAILED') {
                toastStore.open({ message: t('Subscrib_Connect_Create_15'), hideAction: true });
              }
            },
            onError: () => {
              toastStore.open({ message: t('Subscrib_Connect_Create_15'), hideAction: true });
            },
          },
        );
      }
      setPopup(null);
    },
    [connect, redirectUrl, subscription?.id, t, toastStore],
  );

  const handleClose = (oauth?: boolean | undefined) => {
    setIsModalOpen(prev => {
      return { ...prev, isOpen: false };
    });

    if (oauth) {
      //
    }
  };

  const handleAlertStore = (status: 'CONNETED' | 'CONNECTING' | 'WORKFLOW' | 'FAILED') => {
    switch (status) {
      case 'CONNETED':
        alertStore.open({
          title: t('Subscrib_Connect_Create_18'),
          message: t('Connection_Detail_01'),
          confirmName: `${t('Member_User_24')}`,
        });
        break;
      case 'CONNECTING':
        alertStore.open({
          title: t('Subscrib_Connect_Create_18'),
          message: t('Connection_Detail_02'),
          confirmName: `${t('Member_User_24')}`,
        });
        break;
      case 'WORKFLOW':
        alertStore.open({
          title: t('Subscrib_Connect_Create_18'),
          message: t('Subscrib_Connect_Create_08'),
          confirmName: `${t('Member_User_24')}`,
        });
        break;
      case 'FAILED':
        alertStore.open({
          title: t('Connection_Detail_06'),
          message: t('Subscrib_Connect_Create_15'),
          confirmName: `${t('Member_User_24')}`,
        });
        break;
    }
  };

  const handleClickConnectionDeleteBtn = () => {
    alertStore.open({
      title: t('Subscrib_Connect_06'),
      message: t('Subscrib_Connect_09'),
      useCancel: true,
      type: 'error',
      confirmName: t('Subscrib_Detail_BuyRequest_12'),
      onConfirm: () => {
        if (connection?.workflowCount) {
          handleAlertStore('WORKFLOW');
        } else if (connection?.status === 'CONNECTED') {
          handleAlertStore('CONNETED');
        } else if (connectMutatingCount > 0 || connection?.status === 'CONNECTING') {
          handleAlertStore('CONNECTING');
        } else {
          deleteConnection(undefined, {
            onSuccess: () => {
              toastStore.open({ message: t('Subscrib_Connect_Create_17') });
              navigate(`/manager/${authStore.curTenant.id}/subscriptions/connections`);
            },
            onError: (e: unknown) => {
              if (e instanceof AxiosError) {
                if (e.response?.data.errorCode.messageId === 'CONNECTION.ERROR.CANNOT_REMOVE_IN_CURRENT_STATUS') {
                  if (connection?.status === 'CONNECTED') {
                    handleAlertStore('CONNETED');
                  }
                } else if (
                  e.response?.data.errorCode.messageId === 'CONNECTION.ERROR.CANNOT_REMOVE_WHEN_WORKFLOW_EXIST'
                ) {
                  handleAlertStore('WORKFLOW');
                }
              }
            },
          });
        }
      },
    });
  };

  const handleClickDisconnect = () => {
    alertStore.open({
      title: t('Subscrib_Connect_05'),
      message: t('Subscrib_Connect_08'),
      useCancel: true,
      confirmName: t('Subscrib_Connect_Create_11'),
      type: 'error',
      onConfirm: () => {
        if (!connection?.subscription.id) return;
        disconnect(connection.subscription.id);
      },
    });
  };

  const connectSubscription = async (authType: string) => {
    if (authType === 'oauth2' || authType === 'oauth2CustomClient') {
      const { data: url } = await refetch();
      if (!url) {
        toastStore.open({ message: t('Subscrib_Connect_Create_15'), hideAction: true });
        return;
      }

      const oauthUrl = new URL(url);
      oauthUrl.searchParams.set('redirect_uri', redirectUrl);

      const popupWidth = 800;
      const popupHeight = 600;

      // 부모 창의 중앙에 맞게 팝업 창 위치 계산
      const left = window.screenX + (window.innerWidth - popupWidth) / 2;
      const top = window.screenY + (window.innerHeight - popupHeight) / 2;

      // 팝업 창의 위치와 크기 설정
      const windowFeatures = `width=${popupWidth},height=${popupHeight},top=${top},left=${left},resizable=yes,scrollbars=yes`;

      const newPopup = window.open(oauthUrl.toString(), t('Connection_Detail_03'), windowFeatures);
      setPopup(newPopup);
    } else {
      connect(
        { subscriptionId: subscription?.id, useAsyncJob: true },
        {
          onSuccess: data => {
            if (data.status === 'FAILED') {
              toastStore.open({ message: t('Subscrib_Connect_Create_15'), hideAction: true });
            }
          },
          onError: (e: unknown) => {
            if (e instanceof AxiosError) {
              handleAlertStore('FAILED');
            }
          },
        },
      );
    }
  };

  const handleClickConnect = async () => {
    connectSubscription(connection?.connection.connectionInfo.authType);
  };

  const handleSubmitAppLogin = async (conn: SubscriptionConnectionModel) => {
    connectSubscription(conn.connection.connectionInfo.authType);
  };

  const handleClickConnectionModifyBtn = () => {
    if (connection?.status === 'CONNECTED') {
      alertStore.open({
        title: t('Subscrib_Connect_Create_07'),
        message: t('Connection_Detail_02'),
        useCancel: true,
        confirmName: `${t('Member_User_24')}`,
      });
    } else {
      setIsModalOpen({ isOpen: true, type: 'modify' });
    }
  };

  useEffect(() => {
    if (!popup) {
      return undefined;
    }
    window.addEventListener('message', oauthMessageListener, false);

    return () => {
      window.removeEventListener('message', oauthMessageListener, false);
    };
  }, [popup, oauthMessageListener]);

  if (!subscription) {
    return null;
  }

  const productCategory = categoryStore.findRootCategory(subscription.randomCategory.id);

  return (
    <>
      <Container>
        <Header>
          <IconButton onClick={() => navigate(-1)}>
            <ChevronIcon rotateNum={270} />
          </IconButton>
          <strong className="title">{t('Connection.title')}</strong>
        </Header>
        {isSubscriptionLoading ? null : (
          <Body>
            <AppBar>
              <Subscription>
                <StyledAvatar src={subscription?.software.iconUrl} />
                <div className="app-info">
                  <span className="app-name">{subscription?.software.name}</span>
                  <span className="subscription-name">{subscription?.name}</span>
                </div>
              </Subscription>
              <div className="config">
                <StyledButton
                  variant="outline"
                  size="large"
                  paddingHorizontal={24}
                  onClick={handleClickConnectionModifyBtn}
                >
                  <PencilIcon width={20} height={20} color={theme.colors['ic-gray-dark']} />
                  {t('Subscrib_Connect_Create_07')}
                </StyledButton>
                <StyledButton
                  variant="outline"
                  size="large"
                  paddingHorizontal={24}
                  onClick={handleClickConnectionDeleteBtn}
                >
                  <TrashIcon width={20} height={20} color={theme.colors['ic-red-light']} />
                  {t('Subscrib_Connect_06')}
                </StyledButton>
              </div>
            </AppBar>
            <Divider />
            <ContentContainer>
              <ItemContainer>
                <SubscriptionInfo
                  subscription={subscription}
                  productCategory={productCategory}
                  onClickLogin={() => setIsModalOpen({ isOpen: true, type: 'new' })}
                />
              </ItemContainer>
              {connection?.connectionId && (
                <ItemContainer>
                  <ConnectionInfo
                    connection={connection}
                    isConnectionFetching={isConnectionFetching}
                    connectMutatingCount={connectMutatingCount}
                    onClickConnectBtn={handleClickConnect}
                    onClickDisconnectBtn={handleClickDisconnect}
                    onClickModifyBtn={handleClickConnectionModifyBtn}
                  />
                  <UsageWorkflowInfo usageWorkflow={subscription?.usageWorkflow} subscriptionId={subscription.id} />
                </ItemContainer>
              )}
            </ContentContainer>
          </Body>
        )}
        {subscription && (
          <AppLogin
            subscription={subscription}
            connection={connection}
            open={isModalOpen.isOpen}
            onClose={handleClose}
            type={isModalOpen.type}
            onSubmit={handleSubmitAppLogin}
          />
        )}
      </Container>
      <ProfileModal
        open={!!ownerProfileAnchorEl}
        anchorEl={ownerProfileAnchorEl}
        onClose={() => setOwnerProfileAnchorEl(null)}
        userId={subscription ? subscription.owner.id : ''}
      />
    </>
  );
});

const Container = styled.div`
  width: 100%;
  height: calc(100vh - var(--manager-gnb-height));
  overflow: auto;
  display: flex;
  flex-direction: column;
  padding: 0px 32px;
`;

const Header = styled.div`
  display: flex;
  margin: 28px 0px 32px 0px;
  align-items: center;
  gap: 4px;
  & .title {
    ${fonts.Headline6};
    color: ${({ theme: { colors } }) => colors['text-gray-main']};
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 28px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  margin-top: 32px;
  max-width: 1000px;
`;

const ItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 32px;
  width: 100%;
  min-width: 400px;
`;

const StyledButton = styled(Button)`
  box-shadow: 0px 1px 6px 0px rgba(0, 0, 0, 0.06);
  & > span {
    gap: 6px;
  }
  & + & {
    color: ${({ theme }) => theme.colors['text-red-light']};
  }
`;

const AppBar = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  & .config {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
  }
`;

const Subscription = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 2px 0;
  & .app-info {
    display: flex;
    flex-direction: column;
    & .app-name {
      ${fonts.Body1};
      color: ${({ theme: { colors } }) => colors['text-gray-main']};
    }
    & .subscription-name {
      ${fonts.Headline4};
      color: ${({ theme: { colors } }) => colors['text-gray-main']};
    }
  }
`;

const StyledAvatar = styled(Avatar)`
  width: 56px;
  height: 56px;
  border-radius: 8px;
`;
